



































import Vue from 'vue';
import ChristmasCalendar from '@/components/announcementbar/christmas-calendar/ChristmasCalendar.vue';
import ChristmasCalendarToggleBauble from '@/components/announcementbar/christmas-calendar/subcomponents/ChristmasCalendarToggleBauble.vue';
import SessionStorageHelper from '@/helpers/SessionStorageHelper';
import URLHelper from '@/helpers/URLHelper';
import DomPositioningHelper from '@/helpers/DomPositioningHelper';
import ErrorHandlerHelper from '@/helpers/ErrorHandlerHelper';
import EventListenerHelper from '@/helpers/EventListenerHelper';
import InfoBar from '@/components/announcementbar/subcomponents/InfoBar.vue';
import { EBarTypes } from '@/common/enums/barTypes.enum';
import Bar from '@/components/announcementbar/subcomponents/Bar.vue';
import AnnouncementBarService from '@/services/AnnouncementBarService';
import { IBar } from '@/common/interfaces/announcement-bar.interface';
import LocalStorageHelper from '@/helpers/LocalStorageHelper';

interface IAnnouncementBarTimer {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface IAnnouncementBarComponent {
  $refs?: {
    announcementBar: HTMLDivElement;
  };
  EBarTypes: typeof EBarTypes;
  active: boolean;
  areaId: string;
  areaTopPosition: number;
  bar: IBar;
  barHeight: number;
  barSpacer: HTMLDivElement | null;
  body: HTMLBodyElement | null;
  calendarVisible: boolean;
  isPartner: boolean;
  link: string;
  timer: IAnnouncementBarTimer;
  timerInterval: number | null;
  updateBarHeightFunction: null | (() => void);
}

export default Vue.extend({
  name: `AnnouncementBar`,
  components: {
    Bar,
    ChristmasCalendar,
    ChristmasCalendarToggleBauble,
    InfoBar,
  },
  data(): IAnnouncementBarComponent {
    return {
      EBarTypes,
      active: false,
      areaId: `rin-announcement-bar`,
      areaTopPosition: 0,
      bar: {
        backgroundColor: ``,
        calendar: null,
        highlightTextColor: ``,
        infoBackgroundColor: ``,
        infoLink: ``,
        infoTextColor: ``,
        linkColor: ``,
        linkText: ``,
        partnerOnly: false,
        primaryText: ``,
        secondaryText: ``,
        textColor: ``,
      },
      barHeight: 0,
      barSpacer: null,
      body: document.querySelector(`body`),
      calendarVisible: false,
      isPartner: false,
      link: ``,
      timer: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      },
      timerInterval: null,
      updateBarHeightFunction: null,
    };
  },
  computed: {
    barStyle(): string {
      return `background-color: ${this.bar.backgroundColor}; top: ${this.areaTopPosition}px;`;
    },
    isAllowedToSee(): boolean {
      return !(this.bar.partnerOnly && !this.isPartner);
    },
    showCalendarToggleBauble(): boolean {
      return !!this.bar.calendar && (!this.calendarVisible && URLHelper.parsedUrlObject.pathname === `/`);
    },
  },
  async mounted(): Promise<void> {
    EventListenerHelper.setUpEventListeners([
      { callback: this.updateAnnouncementBarListener, name: `update-announcementbar` },
      { callback: this.removeAnnouncementBarListener, name: `remove-announcementbar` },
      { callback: this.repositionContent, name: `resize` },
      { callback: this.handlePositionChange, name: `rin-top-margin-height-changed` },
    ]);
    await this.$announcementBarService.connect();
    this.isPartner = LocalStorageHelper.getPersonalInfo()?.isPartner ?? false;
    this.repositionContent();
  },
  beforeDestroy(): void {
    EventListenerHelper.teardownEventListeners([
      { callback: this.updateAnnouncementBarListener, name: `update-announcementbar` },
      { callback: this.removeAnnouncementBarListener, name: `remove-announcementbar` },
      { callback: this.repositionContent, name: `resize` },
      { callback: this.handlePositionChange, name: `rin-top-margin-height-changed` },
    ]);
    if(typeof this.updateBarHeightFunction === `function`) {
      window.removeEventListener(`resize`, this.updateBarHeightFunction);
    }
  },
  methods: {
    checkIfCalendarShouldBeShownAutomatically(): void {
      if(!this.bar.calendar) {
        this.closeCalendar();
        return;
      }
      const lastSeenDate = SessionStorageHelper.getChristmasCalendarSeen();
      if(lastSeenDate === 0 || new Date().getDate() !== new Date(lastSeenDate).getDate()) {
        this.openCalendar();
      }
    },
    closeCalendar(): void {
      this.calendarVisible = false;
      document.body.style.overflow = `auto`;
    },
    counter(): void {
      if(!this.isServiceAvailable()) {
        this.disableBar();
        return;
      }
      this.timer.seconds--;
      if(this.timer.seconds < 0) {
        this.timer.seconds = 59;
        this.timer.minutes--;
        if(this.timer.minutes < 0) {
          this.timer.minutes = 59;
          this.timer.hours--;
          if(this.timer.hours < 0) {
            this.timer.hours = 23;
            this.timer.days--;
            if(this.timer.days < 0) {
              this.active = false;
            }
          }
        }
      }
    },
    disableBar(): void {
      this.active = false;
      this.$nextTick(this.repositionContent);
    },
    handlePositionChange(): void {
      if(this.body) {
        DomPositioningHelper.setBodyPosition(this.body);
      }
      this.areaTopPosition = DomPositioningHelper.getAreaTopPosition(this.areaId);
    },
    isServiceAvailable(): boolean {
      return AnnouncementBarService.isConnected && this.active;
    },
    openCalendar(): void {
      this.calendarVisible = true;
      document.body.style.overflow = `hidden`;
    },
    removeAnnouncementBarListener(): void {
      this.disableBar();
    },
    repositionContent(): void {
      DomPositioningHelper.registerArea(2, this.areaId, this.barHeight);
    },
    updateAnnouncementBarListener(evt: Event) : void {
      try {
        const data = JSON.parse((evt as CustomEvent).detail);
        this.active = true;
        this.bar = data.bar;
        this.link = data.link;
        const daysMsDuration = 1000 * 60 * 60 * 24;
        const hoursMsDuration = 1000 * 60 * 60;
        const minutesMsDuration = 1000 * 60;
        const secondsMsDuration = 1000;
        let difference = data.until - Date.now();
        const days = Math.floor(difference / daysMsDuration);
        difference -= days * daysMsDuration;
        const hours = Math.floor(difference / hoursMsDuration);
        difference -= hours * hoursMsDuration;
        const minutes = Math.floor(difference / minutesMsDuration);
        difference -= minutes * minutesMsDuration;
        const seconds = Math.ceil(difference / secondsMsDuration);
        this.timer = {
          days,
          hours,
          minutes,
          seconds,
        };
        if(!this.timerInterval) {
          this.timerInterval = window.setInterval(this.counter, 1000);
        }
        this.updateBarHeightFunction = window._.debounce(this.updateBarHeight, 125);
        window.addEventListener(`resize`, this.updateBarHeightFunction);
        this.$nextTick(() => {
          this.updateBarHeight();
          this.repositionContent();
        });
        this.checkIfCalendarShouldBeShownAutomatically();
      } catch (err) {
        ErrorHandlerHelper.handleError(err, { logToConsole: true });
      }
    },
    updateBarHeight(): void {
      this.barHeight = this.$refs?.announcementBar?.clientHeight ?? 0;
    },
  },
});
